<template>
    <div :class="['flex', imageTextSpacing]">
        <div :class="[imageContainerClasses]">
            <FileDownloader v-if="item.file && item.type_id == 'file'" :link="item.file"
                :useAccentBg="item.modelClass == panelModalClass" class="rounded-lg"
                :small="displayVariant == 'sidebar-listitem'" />
            <component v-else :is="url != null ? 'Link' : 'div'" :href="url" class="size-full">
                <ModelImage :src="item.image" :alt="item.name" :modelClass="item.modelClass"
                    :useAccentBg="item.modelClass == panelModalClass" fallbackBorder="border border-resource-accent/50"
                    imageClasses="size-full" class="size-full rounded-lg" :clickable="url != null"
                    :typeId="item.type_id" />
            </component>
        </div>
        <div :class="[
            'w-2/3 xl:min-w-2/3 xl:w-auto flex-grow relative',
            bodyClasses,
        ]">
            <div class="flex 3xl:relative">
                <div :class="[infoSpacing, 'flex items-center']">
                    <p v-if="
                        showDates && item.modelClass == 'event' && item.start && item.end
                    " v-text="`${$moment(item.start).format('L')} - ${$moment(
                        item.end
                    ).format('L')}`
                        " :class="[infoTextClasses, 'shrink-0']" />
                    <p v-else-if="showDates && item.modelClass != 'event'" v-text="$moment(item.created_at).format('L')"
                        :class="[infoTextClasses, 'shrink-0']" />
                    <div v-if="displayVariant != 'sidebar-listitem'" class="flex items-center">
                        <TypeTag v-if="item.type" :type="item.type" classes="bg-white block truncate" />
                    </div>
                </div>

                <div :class="[
                    'absolute z-popover',
                    {
                        'right-0 bottom-0 3xl:bottom-auto 3xl:top-1/2 translate-y-1/3 3xl:-translate-y-1/2':
                            isToTheRight,
                        'right-0 top-0 -translate-y-1/4': !isToTheRight,
                    },
                ]" v-if="showOptionsButton && options && options.length > 0">
                    <ButtonPopoverMenu :items="options">
                        <template #button="{ open }">
                            <div :class="[
                                open
                                    ? `border-${panelModalClass}-accent bg-${panelModalClass}-accent text-${panelModalClass}`
                                    : 'border-transparent',
                                `border hover:border-${panelModalClass}-accent size-10 p-2 rounded-full transition duration-button-hover focus-style`,
                            ]">
                                <PhDotsThreeOutline class="flex-grow" weight="fill" />
                            </div>
                        </template>
                    </ButtonPopoverMenu>
                </div>
            </div>
            <TruncateTooltip>
                <component :is="url != null ? 'DynamicLink' : 'div'" :href="url" :class="[
                    titleClasses,
                    'underline-hover text-gray-900 font-header',
                ]" v-text="item.name" />
            </TruncateTooltip>

            <TruncateTooltip v-if="
                (item.short_description || item.description) &&
                ['maincolumn', 'maincolumn-relation-panel'].includes(
                    displayVariant
                )
            " maxWidth="max-w-[34rem]">
                <p v-text="item.short_description || item.description" :class="[
                    'line-clamp-2',
                    {
                        'text-sm':
                            displayVariant == 'maincolumn-relation-panel',
                    },
                ]" />
            </TruncateTooltip>

            <Link v-else-if="
                item.is_linked_resource && displayVariant == 'sidebar'
            " :href="$localizedRoute('resource.show', item.id)" class="">
            <PrimaryButton size="xs" class="py-0 text-sm">
                <p class v-text="$lang.get('Show the link')" />
            </PrimaryButton>
            </Link>
        </div>
    </div>
</template>

<script>
import ModelImage from '@/Components/ModelComponents/ModelImage.vue'
import FileDownloader from '@/Components/ModelComponents/FileDownloader.vue'
import TruncateTooltip from '@/Components/Utilities/TruncateTooltip.vue'
import TypeTag from '@/Components/Elements/Tags/TypeTag.vue'
import PrimaryButton from '@/Components/Elements/PrimaryButton.vue'
import { Link } from '@inertiajs/vue3'
import DynamicLink from '@/Components/Utilities/DynamicLink.vue'

import { PhDotsThreeOutline } from '@phosphor-icons/vue'
import ButtonPopoverMenu from '@/Components/Utilities/ButtonPopoverMenu.vue'

export default {
    name: 'DetailedListItem',

    components: {
        ModelImage,
        Link,
        DynamicLink,
        PrimaryButton,
        FileDownloader,
        TruncateTooltip,
        TypeTag,
        ButtonPopoverMenu,
        PhDotsThreeOutline,
    },

    emits: ['deleteModel'],

    props: {
        title: {
            type: String,
            default: 'Related resources',
        },
        isSideBarRelations: {
            type: Boolean,
            default: false,
        },
        item: {
            type: Object,
            default: () => {
                return {
                    image: null,
                    name: null,
                    file: null,
                    created_at: null,
                    type: null,
                    type_id: null,
                    data: {},
                }
            },
            validator: (value) => {
                return (
                    Object.hasOwn(value, 'name') &&
                    Object.hasOwn(value, 'created_at') &&
                    Object.hasOwn(value, 'url') &&
                    Object.hasOwn(value, 'type') &&
                    Object.hasOwn(value, 'type_id')
                )
            },
        },
        // The modelClass where the panel is displayed
        panelModalClass: {
            type: String,
            required: true,
        },

        displayVariant: {
            type: String,
            required: true,
            validator: (value) => {
                return [
                    'sidebar',
                    'maincolumn',
                    'sidebar-listitem',
                    'maincolumn-relation-panel',
                ].includes(value)
            },
        },
        isToTheRight: {
            type: Boolean,
            default: false,
        },
        generateUrl: {
            type: Boolean,
            default: false,
        },
        showOptionsButton: {
            type: Boolean,
            default: false,
        },
    },

    created() {
        if (this.showOptionsButton) {
            this.options = []
            if (this.item.can?.edit) {
                this.options.push({
                    text: this.$lang.get('Edit'),
                    href: this.$localizedRoute(
                        `${this.item.modelClass}.edit`,
                        this.item.id
                    ),
                    type: 'link',
                })
            }
            if (this.item.can?.delete) {
                this.options.push({
                    text: this.$lang.get('Delete'),
                    type: 'button',
                    action: () => {
                        this.$emit('deleteModel', this.item)
                    },
                })
            }
        }
    },

    computed: {
        showDates() {
            return this.isSideBarRelations ? false : true
        },
        url() {
            if (this.item.is_linked_resource) {
                return this.$localizedRoute(
                    'resource.show',
                    this.item.resource_id
                )
            }
            if (this.generateUrl) {
                return this.$localizedRoute(
                    this.item.modelClass + '.show',
                    this.item.id
                )
            }
            return this.item.url || null
        },

        imageContainerClasses() {
            if (this.displayVariant == 'sidebar-listitem') {
                return 'h-[50px] aspect-[5/4]'
            } else if (this.displayVariant == 'sidebar' && this.item.name.length > 29) {
                return 'h-[100px] aspect-square aspect-[5/4]'
            } else if (this.displayVariant == 'sidebar') {
                return 'h-[80px] aspect-square 2xl:aspect-[5/4]'
            } else if (this.displayVariant == 'maincolumn-relation-panel') {
                return 'h-[100px] aspect-square 2xl:aspect-[5/4]'
            } else if (this.displayVariant == 'maincolumn') {
                return 'h-[150px] aspect-[5/4]'
            }
        },

        bodyClasses() {
            if (this.displayVariant == 'sidebar-listitem') {
                return 'h-[50px] space-y-1'
            } else if (this.displayVariant == 'sidebar') {
                return 'h-[80px] space-y-1'
            } else if (this.displayVariant == 'maincolumn-relation-panel') {
                return 'h-[100px] space-y-1'
            } else if (this.displayVariant == 'maincolumn') {
                return 'space-y-2'
            }
        },

        titleClasses() {
            if (this.displayVariant == 'sidebar-listitem') {
                return 'line-clamp-2'
            } else if (
                this.displayVariant == 'sidebar' ||
                this.displayVariant == 'maincolumn-relation-panel'
            ) {
                return 'text-lg font-semibold line-clamp-2'
            } else if (this.displayVariant == 'maincolumn') {
                return 'text-2xl font-medium line-clamp-2'
            }
        },

        imageTextSpacing() {
            if (this.isSidebarItem) {
                if (this.isToTheRight) {
                    return 'gap-x-2 2xl:gap-x-4'
                } else {
                    return 'gap-x-5'
                }
            } else {
                return 'gap-x-6'
            }
        },

        infoSpacing() {
            if (this.isSidebarItem) {
                if (this.isToTheRight) {
                    return 'gap-x-2'
                } else {
                    return 'gap-x-4'
                }
            } else {
                return 'gap-x-4'
            }
        },

        infoTextClasses() {
            if (
                this.isSidebarItem ||
                this.displayVariant == 'maincolumn-relation-panel'
            ) {
                return this.isToTheRight ? 'text-xs' : 'text-sm'
            }
            return 'text-lg'
        },

        isSidebarItem() {
            return (
                this.displayVariant == 'sidebar' ||
                this.displayVariant == 'sidebar-listitem'
            )
        },
    },
}
</script>
